import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GameState, Player, RoundSubmission } from '../../types/game.types';
import {
  calculateAppliedBonuses,
  calculateRascalRoundScore,
  createGameId,
  createRoundDetail,
  getCardsDealtForRound,
  getRoundPlan
} from '../../utils/gameRules';
import { normalizePlayerAppearance } from '../mascots/appearance';
import { PLAYER_COLORS } from '../../utils/constants';

export const DEFAULT_GAME_SETTINGS: GameState['settings'] = {
  hideScores: false,
  showPreviousRounds: true,
  showGraph: true,
  autoSave: true,
  persistLastPlayers: true,
  hideRulesPanel: false,
  completeRules: false,
  roundVariant: 'classic',
  rascalScoring: false
};

function createSimplifiedRoundPlan(): number[] {
  return Array.from({ length: 10 }, (_, index) => index + 1);
}

function createRoundPlan(playerCount: number, useCompleteRules: boolean, variant: GameState['settings']['roundVariant']): number[] {
  if (!useCompleteRules) return createSimplifiedRoundPlan();
  return getRoundPlan(variant).map(cards => getCardsDealtForRound(cards, playerCount));
}

export function createInitialGameState(): GameState {
  return {
    players: [],
    currentRound: 1,
    settings: { ...DEFAULT_GAME_SETTINGS },
    rulesMode: 'simplified',
    roundPlan: createSimplifiedRoundPlan(),
    roundVariant: null,
    rascalScoring: false,
    gameId: null,
    createdAt: null,
    completedAt: null
  };
}

function normalisePlayer(player: Player, index = 0): Player {
  return {
    ...player,
    appearance: normalizePlayerAppearance(player.appearance, player.id, index, PLAYER_COLORS.length),
    predictions: player.predictions || [],
    results: player.results || [],
    roundDetails: player.roundDetails || []
  };
}

function beginGame(state: GameState, players: Player[]): void {
  const rulesMode = state.settings.completeRules ? 'complete' : 'simplified';
  state.players = players.map(normalisePlayer).map(player => ({
    ...player,
    score: 0,
    predictions: [],
    results: [],
    roundDetails: []
  }));
  state.currentRound = 1;
  state.rulesMode = rulesMode;
  state.roundPlan = createRoundPlan(state.players.length, rulesMode === 'complete', state.settings.roundVariant);
  state.roundVariant = rulesMode === 'complete' ? state.settings.roundVariant : null;
  state.rascalScoring = rulesMode === 'complete' && state.settings.rascalScoring;
  state.gameId = createGameId();
  state.createdAt = Date.now();
  state.completedAt = null;
}

function hasLeadingTie(players: Player[]): boolean {
  if (players.length < 2) return false;
  const scores = [...players].map(player => player.score).sort((left, right) => right - left);
  return scores[0] === scores[1];
}

const gameSlice = createSlice({
  name: 'game',
  initialState: createInitialGameState(),
  reducers: {
    setPlayers: (state, action: PayloadAction<Player[]>) => {
      beginGame(state, action.payload);
    },
    clearGame: state => {
      const settings = { ...state.settings };
      Object.assign(state, createInitialGameState(), { settings });
    },
    resetGame: state => {
      if (!state.players.length) return;
      beginGame(state, state.players);
    },
    submitRound: (state, action: PayloadAction<RoundSubmission>) => {
      if (state.completedAt) return;

      const { predictions, results, bonuses = [] } = action.payload;
      const cardsDealt = state.roundPlan[state.currentRound - 1] ?? state.currentRound;
      const usesRascalScoring = state.rulesMode === 'complete' && state.rascalScoring;

      state.players = state.players.map((player, index) => {
        const prediction = predictions[index] ?? 0;
        const result = results[index] ?? 0;
        const appliedBonuses = state.rulesMode === 'complete'
          ? calculateAppliedBonuses(bonuses[index] || [], prediction, result, usesRascalScoring)
          : [];
        const bidPoints = usesRascalScoring
          ? calculateRascalRoundScore(prediction, result, cardsDealt)
          : undefined;
        const detail = createRoundDetail(
          state.currentRound,
          cardsDealt,
          prediction,
          result,
          appliedBonuses,
          bidPoints,
          usesRascalScoring ? 'rascal' : 'standard'
        );

        return {
          ...player,
          score: player.score + detail.totalPoints,
          predictions: [...player.predictions, prediction],
          results: [...player.results, result],
          roundDetails: [...player.roundDetails, detail]
        };
      });

      state.currentRound += 1;
      if (state.currentRound > state.roundPlan.length) state.completedAt = Date.now();
    },
    addTieBreakerRound: state => {
      if (state.rulesMode !== 'complete' || !state.completedAt || !hasLeadingTie(state.players)) return;
      const cardsDealt = state.roundPlan[state.roundPlan.length - 1] || 1;
      state.roundPlan.push(cardsDealt);
      state.currentRound = state.roundPlan.length;
      state.completedAt = null;
    },
    updateSettings: (state, action: PayloadAction<Partial<GameState['settings']>>) => {
      state.settings = {
        ...state.settings,
        ...action.payload
      };
    }
  }
});

export const {
  setPlayers,
  clearGame,
  submitRound,
  addTieBreakerRound,
  resetGame,
  updateSettings
} = gameSlice.actions;

export default gameSlice.reducer;
